'use client';

import { createContext, FC, PropsWithChildren, useCallback, useMemo, useReducer, useState } from 'react';

import { ShoppingCartUtils } from '@ravago/shared/next-data-access/client';
import { OrderableProduct, Quantity } from '@ravago/shared/page-data/models/elements';
import { PostShoppingCartItem, Source } from '@ravago/shared/page-data/models/features';

import { initialState, ShoppingCartReducer, ShoppingCartState } from '../reducers/shopping-cart-reducer';

interface ContextType {
   state: ShoppingCartState;
   isAddingToCart: boolean;
   addToCart: (source: Source, orderableProduct: OrderableProduct, packaging: string, quantity: Quantity, allowOverwrite?: boolean) => void;
   resetCartValidation: () => void;
   removeFromCart: (success: boolean) => void;
}

export const ShoppingCartContext = createContext<ContextType>({
   state: initialState,
   isAddingToCart: false,
   addToCart: () => {},
   removeFromCart: () => {},
   resetCartValidation: () => {}
});

export const ShoppingCartProviders: FC<PropsWithChildren> = ({ children }) => {
   const [state, dispatch] = useReducer(ShoppingCartReducer, initialState);
   const [isAddingToCart, setIsAddingToCart] = useState(false);

   const addToCart = useCallback(
      async (source: Source, orderableProduct: OrderableProduct, packaging: string, quantity: Quantity, allowOverwrite?: boolean) => {
         if (isAddingToCart) return;

         setIsAddingToCart(true);
         try {
            const item: PostShoppingCartItem = {
               productId: orderableProduct.id,
               unitPriceSource: source,
               quantity: { amount: quantity.amount, unitOfMeasure: quantity.unitOfMeasure }
            };

            if (!allowOverwrite) {
               const shoppingCart = await ShoppingCartUtils.getShoppingCart();
               if (shoppingCart) {
                  const existingItem = shoppingCart.items.find((i) => i.product.id === item.productId && i.packaging === packaging);
                  if (existingItem && existingItem.quantity.amount !== item.quantity.amount) {
                     dispatch({
                        type: 'duplicate_product',
                        value: {
                           type: 'DUPLICATE',
                           duplicateCartItem: {
                              source,
                              orderableProduct,
                              quantity,
                              packaging
                           },
                           existingItem
                        }
                     });
                     return;
                  }
               }
            }

            const addToCartResponse = await ShoppingCartUtils.addToShoppingCart(item);

            if (addToCartResponse.success) {
               dispatch({ type: 'add_to_cart', value: { type: 'ADDED' } });
            } else {
               dispatch({
                  type: 'add_to_cart',
                  value: {
                     type: 'ERROR',
                     productName: orderableProduct.description,
                     validationErrors: addToCartResponse.problems?.length
                        ? addToCartResponse.problems
                        : [
                             {
                                code: 'shopping-cart.validation.error',
                                message: 'shopping-cart.validation.error',
                                level: 'ERROR'
                             }
                          ]
                  }
               });
            }
         } finally {
            setIsAddingToCart(false);
         }
      },
      [isAddingToCart]
   );

   const resetCartValidation = useCallback(() => {
      dispatch({ type: 'reset', value: {} });
   }, []);

   const removeFromCart = useCallback((success: boolean) => {
      if (success) dispatch({ type: 'remove_from_cart', value: { type: 'REMOVED' } });
      else
         dispatch({
            type: 'remove_from_cart',
            value: {
               type: 'ERROR',
               validationErrors: [
                  {
                     code: 'shopping-cart.validation.error',
                     message: 'shopping-cart.validation.error',
                     level: 'ERROR',
                     removeError: true
                  }
               ]
            }
         });
   }, []);

   const contextValue = useMemo(
      () => ({
         state,
         isAddingToCart,
         addToCart,
         resetCartValidation,
         removeFromCart
      }),
      [state, isAddingToCart, addToCart, resetCartValidation, removeFromCart]
   );

   return <ShoppingCartContext value={contextValue}>{children}</ShoppingCartContext>;
};
